import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Dropdown } from 'antd';
import { CheckOutlined, DownOutlined, CaretRightOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton } from '@mui/material';
import COLOR from 'utils/constants/color';
import { useCurrentUser } from 'store/users/selectors';
import { DateRangeDropdown } from 'components/GradeDownloader';

const StyledMenuItem = styled.div`
  background-color: #fff;
  color: ${COLOR.green100};
  font-size: 14px;
  padding: 8px 20px;
  cursor: pointer;

  &:hover {
    background-color: #e7eaee;
  }
`;

const SectionTitle = styled.div`
  font-size: 11px;
  color: #b0bcc9;
  margin: 8px 20px 4px;
  font-weight: bold;
`;

const Button = styled.button`
    background-color: transparent;
    border: none;
    color: ${p => p.isIpad ? COLOR.black : COLOR.green200};
    cursor: pointer;
    outline: none;
    transition: all 300ms ease;
     display: flex;
    flex-direction:row;
    align-items:center;
    margin-left:${p => p.isIpad ? "0px" : "8px"};
    margin-right:8px;
    &:hover {
        color: ${COLOR.green}
    }
`;

const CheckIcon = styled(CheckOutlined)`
 margin-left: 8px;
 color:${COLOR.green100};
  &:hover {
      background-color: #e7eaee;
    }
`;

const ShowCheck = React.memo(({ checked }) => (
  checked ? <CheckIcon /> : null
));

const ALLUSER_FLAG = [
    { title: "Excused", value: 1, label: "Marked 'Excused'", key: 'excused' },
    { title: "Incomplete", value: 2, label: "Marked 'Incomplete'", key: 'incomplete' },
    { title: "Missing", value: 3, label: "Marked 'Missing'", key: 'missing' },
];
const SCHOOLOGY_ALLUSER_FLAG = [
    { title: "Exempt", value: 1, label: "Marked 'Exempt'" , key:'exempt' },
    { title: "Incomplete", value: 2, label: "Marked 'Incomplete'", key: 'incomplete' },
    { title: "Missing", value: 3, label: "Marked 'Missing'", key: 'missing' },
    { title: "Absent", value: 5, label: "Marked 'Absent'", key: 'absent' },
    { key: 'late', label: "Marked 'Late'" },
];
const CANVAS_FLAG_WITH_COMPLETE = [
    { title: "Excused", value: 1, label: "Marked 'Excused'", key: 'excused' },
    { title: "Missing", value: 3, label: "Marked 'Missing'", key: 'missing' }
];
const HIGHLIGHT_OPTIONS = [
    { key: 'needs-grading', label: "Needs Grading" },
    { key: 'not-submitted', label: "Not Submitted" }
];

const ViewDropdown = ({ config = {}, onChange, isTabMenu, show_hiddent, dateRange, onDateRangeChange, activeSem, parentOrgId }) => {
    const currentUser = useCurrentUser();
    const [showHiddenAssignment, setShowHiddenAssignment] = useState(Boolean(config && config['show_hidden']));
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const isFilterActive = Boolean(dateRange && dateRange !== 'all');
    
    useEffect(() => {
        setShowHiddenAssignment(Boolean(config && config['show_hidden']));
    }, [config]);
    
    const menu_data = useMemo(() => {
        const isSchoologyUser = !!(currentUser && currentUser.schoologyid)
        const isCanvasUser = !!(currentUser && currentUser.canvas_id);
        const isInactiveFilter = Boolean(config && config['view_inactive']);
        const toggleLabel = isInactiveFilter ? 'Active' : 'Inactive';
        const menu_ites = [];
        if (isSchoologyUser) {
            menu_ites.push(...SCHOOLOGY_ALLUSER_FLAG)
        } else if (isCanvasUser) {
            menu_ites.push(...CANVAS_FLAG_WITH_COMPLETE)
        } else {
            menu_ites.push(...ALLUSER_FLAG)
        }
        menu_ites.push(...HIGHLIGHT_OPTIONS)
        if(!showHiddenAssignment) {
            menu_ites.push({ key: 'show_hidden', label: "View Hidden" })
        }else{
            menu_ites.push({ key: 'hide_hidden', label: "Hide Hidden" })
        }
        menu_ites.push({ key: 'filter_by_date', label: "Filter" })
        return menu_ites;
    }, [currentUser, config, showHiddenAssignment])

    const handleMenuClick = useCallback(({ key }) => {
        if (key === 'filter_by_date') {
          if (onDateRangeChange != null && dateRange != null && activeSem != null && parentOrgId != null) {
            setFilterModalOpen(true);
          }
          return;
        }
        if (key === 'show_hidden') {
          setShowHiddenAssignment(true);
        } else if (key === 'hide_hidden') {
          setShowHiddenAssignment(false);
        }
        if (onChange) {
          const exists = key in config;
          const val = menu_data.find(p => p.key === key);
      
          const newConfig = { ...config };
      
          if (key === 'toggle_view_filter') {
            const currentlyActive = Boolean(config && config['view_active']);
            const currentlyInactive = Boolean(config && config['view_inactive']);
            delete newConfig['view_active'];
            delete newConfig['view_inactive'];
            if (!currentlyActive && !currentlyInactive) {
              newConfig['view_inactive'] = { key: 'view_inactive', label: 'View Inactive' };
            } else if (currentlyActive) {
              newConfig['view_inactive'] = { key: 'view_inactive', label: 'View Inactive' };
            } else if (currentlyInactive) {
              newConfig['view_active'] = { key: 'view_active', label: 'View Active' };
            }
            onChange(newConfig);
            return;
          }
      
          if (key === 'hide_hidden') {
            delete newConfig['show_hidden'];
            onChange(newConfig);
            return;
          }
      
          if (exists) {
            delete newConfig[key];
          } else if (val) {
            newConfig[key] = val;
          }
      
          onChange(newConfig);
        }
      }, [config, onChange, menu_data, onDateRangeChange, dateRange, activeSem, parentOrgId]);

  const menu = {
    items: [
      {
        key: 'section-title',
        label: <SectionTitle>HIGHLIGHT CELLS</SectionTitle>,
        disabled: true,
        style: { cursor: 'default' }
      },
      ...menu_data.map(({ key, label }) => ({
        key,
        label: (
          <StyledMenuItem onClick={() => handleMenuClick({ key })}>
            {label} <ShowCheck checked={key === 'hide_hidden' ? Boolean(config['show_hidden']) : key === 'filter_by_date' ? isFilterActive : Boolean(config[key])} />
          </StyledMenuItem>
        )
      }))
    ]
  };
 
  return (
    <>
      <Dropdown menu={menu} trigger={['click']} placement="bottomLeft">
        <Button onClick={(e) => { e.preventDefault() }} isIpad={isTabMenu} >
          View  {!isTabMenu ? <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} /> : <CaretRightOutlined style={{ fontSize: 10, marginLeft: 4 }}/>}
        </Button>
      </Dropdown>
      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>Filter by date range</DialogTitle>
        <DialogContent style={{ paddingTop: 8 }}>
          <DateRangeDropdown
            value={dateRange}
            onDatePicked={onDateRangeChange}
            activeSem={activeSem}
            parentOrgId={parentOrgId}
            dateOnly
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setFilterModalOpen(false)} color="primary">
            Close
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewDropdown;